/**
 * Animated Agio logo — letters draw on one-by-one via stroke-dashoffset
 * (port of the original portal's loader). Fetches /brand/agio-logo.svg,
 * inlines it, groups paths into letters by horizontal proximity, animates.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '../utils.js';

const PER_LETTER_MS = 600;
const STAGGER_MS = 450;
const GAP_THRESHOLD = 0.06;
const MIN_HEIGHT_RATIO = 0.15;

export function AgioLogoAnimated({ className }: { className?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [svgText, setSvgText] = useState<string | null>(null);

  useEffect(() => {
    fetch('/brand/agio-logo.svg')
      .then((r) => r.text())
      .then(setSvgText)
      .catch(() => setSvgText(null));
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svgText) return;
    const svg = host.querySelector('svg');
    if (!svg) return;
    svg.setAttribute('class', 'w-full h-auto overflow-visible');
    svg.querySelectorAll('path').forEach((p) => {
      p.style.fill = 'currentColor';
      p.style.stroke = 'currentColor';
      p.style.strokeWidth = '1';
      (p.style as CSSStyleDeclaration & { vectorEffect?: string }).vectorEffect = 'non-scaling-stroke';
    });

    const paths = Array.from(svg.querySelectorAll('path'));
    const r = svg.getBoundingClientRect();
    if (r.width === 0) return;

    const meta = paths.map((p) => {
      const b = p.getBoundingClientRect();
      let len = 0;
      try {
        len = p.getTotalLength();
      } catch {
        /* ignore */
      }
      return {
        p,
        left: (b.left - r.left) / r.width,
        right: (b.right - r.left) / r.width,
        animate: b.height / r.height >= MIN_HEIGHT_RATIO && len > 0,
        len,
      };
    });

    meta.filter((m) => !m.animate).forEach((m) => {
      m.p.style.fillOpacity = '1';
      m.p.style.stroke = 'none';
    });

    // Group into letters by horizontal proximity
    const sorted = meta.filter((m) => m.animate).sort((a, b) => a.left - b.left);
    const groups: (typeof sorted)[] = [];
    let right = -1;
    for (const it of sorted) {
      if (groups.length === 0 || it.left > right + GAP_THRESHOLD) groups.push([it]);
      else groups[groups.length - 1].push(it);
      right = Math.max(right, it.right);
    }

    const anims: Animation[] = [];
    groups.forEach((grp, i) => {
      grp.forEach((m) => {
        m.p.style.fillOpacity = '0';
        m.p.style.strokeDasharray = String(m.len);
        m.p.style.strokeDashoffset = String(m.len);
        anims.push(
          m.p.animate(
            [
              { strokeDashoffset: m.len, fillOpacity: 0, offset: 0 },
              { strokeDashoffset: 0, fillOpacity: 0, offset: 0.6 },
              { strokeDashoffset: 0, fillOpacity: 1, offset: 1 },
            ],
            { duration: PER_LETTER_MS, delay: i * STAGGER_MS, fill: 'both', easing: 'cubic-bezier(.6,0,.2,1)' },
          ),
        );
      });
    });
    return () => anims.forEach((a) => a.cancel());
  }, [svgText]);

  if (!svgText) return null;
  return (
    <div
      ref={hostRef}
      className={cn('w-[min(420px,70vw)] text-foreground', className)}
      dangerouslySetInnerHTML={{ __html: svgText }}
    />
  );
}
